import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { verifyEmail, resendVerificationEmail } from '../lib/auth';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setError('Ugyldig verifiseringslenke');
      setLoading(false);
      return;
    }

    // Verify email automatically
    verifyEmail(token)
      .then((data) => {
        setSuccess(true);
        setEmail(data.email || '');
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Kunne ikke verifisere e-post');
        setLoading(false);
      });
  }, [searchParams]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResendSuccess(false);
    setResending(true);

    try {
      await resendVerificationEmail(resendEmail);
      setResendSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Kunne ikke sende e-post');
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Loader2 className="animate-spin h-12 w-12 text-kf-primary mx-auto" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Verifiserer e-post...
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Vennligst vent mens vi verifiserer din e-postadresse.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              E-post verifisert!
            </h2>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <p className="text-sm text-green-800">
              Din e-postadresse {email && <strong>({email})</strong>} har blitt verifisert.
            </p>
            <p className="text-sm text-green-800 mt-2">
              Du kan nå logge inn på kontoen din.
            </p>
          </div>
          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-kf-primary hover:bg-kf-secondary transition-colors"
            >
              Gå til innlogging
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Error state - show resend option
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Verifisering feilet
          </h2>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>

        {/* Resend verification form */}
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Send verifiseringslenke på nytt
          </h3>
          {resendSuccess ? (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-sm text-green-800">
                En ny verifiseringslenke har blitt sendt til {resendEmail}.
              </p>
            </div>
          ) : (
            <form onSubmit={handleResend} className="space-y-4">
              <div>
                <label htmlFor="email" className="label">
                  E-postadresse
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  className="input"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="din@epost.no"
                />
              </div>
              <button
                type="submit"
                disabled={resending}
                className="btn btn-primary w-full"
              >
                {resending ? 'Sender...' : 'Send på nytt'}
              </button>
            </form>
          )}
        </div>

        <div className="text-center mt-4">
          <Link
            to="/login"
            className="text-sm text-kf-primary hover:text-kf-secondary"
          >
            Tilbake til innlogging
          </Link>
        </div>
      </div>
    </div>
  );
}
